// Cell formatting for the Order Tracking Sheet. Pure functions, so the table and the
// Excel export agree on what a cell says. Column keys are pillup-api's
// classes/OrderTracking/Constants.js COLUMNS[].key -- the API is the source of truth for
// which columns exist; this file only knows how to print a value of each kind.

export const AVAILABILITY_META = {
    AVAILABLE: { label: "Available", dot: "bg-elm-500", chip: "bg-elm-50 text-elm-700 border-elm-200" },
    DERIVED: { label: "Derived", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200" },
    PARTIAL: { label: "Partial", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200" },
    MISSING: { label: "Not captured", dot: "bg-rose-400", chip: "bg-rose-50 text-rose-700 border-rose-200" },
};

const DATE_KEYS = new Set(["orderDate", "startDate", "paymentDate", "leadCreationDate", "dispatchDate", "deliveryDate"]);
const MONEY_KEYS = new Set(["totalAmount", "invoiceAmount", "serviceCharge", "codPremiumCharge", "referralAmount", "amountReceived", "refundPendingCod"]);
const DAY_KEYS = new Set(["transitDays", "tatDispatch", "tatOrderDispatch"]);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const isEmpty = (value) => value === null || value === undefined || value === "";

/** "08 Oct 2026" in IST -- ops read these against an IST working day. */
export function formatDate(value) {
    if (isEmpty(value)) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const ist = new Date(date.getTime() + 330 * 60 * 1000);
    const dd = String(ist.getUTCDate()).padStart(2, "0");
    return `${dd} ${MONTHS[ist.getUTCMonth()]} ${ist.getUTCFullYear()}`;
}

export function formatMoney(value) {
    if (isEmpty(value)) return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    const abs = Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    return n < 0 ? `-₹${abs}` : `₹${abs}`;
}

export function formatDays(value) {
    if (isEmpty(value)) return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return `${n.toFixed(1)} d`;
}

export function kindOf(key) {
    if (DATE_KEYS.has(key)) return "date";
    if (MONEY_KEYS.has(key)) return "money";
    if (DAY_KEYS.has(key)) return "days";
    if (key === "parcelWeight") return "weight";
    return "text";
}

/** Plain-text value of one cell, as the table shows it and the export writes it. */
export function formatCell(key, value) {
    if (isEmpty(value)) return "";
    switch (kindOf(key)) {
        case "date": return formatDate(value);
        case "money": return formatMoney(value);
        case "days": return formatDays(value);
        case "weight": return `${value} kg`;
        default: return String(value);
    }
}

export const isNumericKind = (key) => ["money", "days", "weight"].includes(kindOf(key));

/** Header row + body rows for DownLoadTable, in catalog order. */
export function buildExport(columns, rows) {
    const header = columns.map((c) => c.label);
    const body = rows.map((row) => columns.map((c) => formatCell(c.key, row[c.key])));
    return { header, body };
}

/**
 * Consecutive columns sharing a group -> one band cell spanning them, for the grouped
 * header row. Groups are not contiguous in the sheet's own order (Remarks appears at
 * both ends), so this follows runs rather than collecting by group.
 */
export function groupRuns(columns) {
    return columns.reduce((runs, col) => {
        const last = runs[runs.length - 1];
        if (last && last.group === col.group) last.span += 1;
        else runs.push({ group: col.group, span: 1, firstKey: col.key });
        return runs;
    }, []);
}
